/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import PropTypes from 'prop-types';
import { BodyText, Container, HeadingText, Link } from '@paypalcorp/pp-react';
import _get from 'lodash/get';
import { getComplaintsPageInfo, isComplaintsCountryLocaleEnabled } from '../../lib/globalHelper';
import i18n from '../../lib/reacti18n';
import Page from '../../lib/page';

const COMPLAINTS_PROCESS_CODE_LINK_SK =
  'https://paypalobjects.com/helpcenter/documents/PayPal_Slovakia_Complaints_Processing_Code_12102022.pdf';

const complaintsTranslations = {
  introText: i18n.message('smarthelp/complaints.complaintsPage.introText'),
  hereText: i18n.message('smarthelp/complaints.complaintsPage.hereText'),
  nextHeader: i18n.message('smarthelp/complaints.complaintsPage.nextHeader'),
  nextHeaderText: i18n.message('smarthelp/complaints.complaintsPage.nextHeaderText'),
  nextHeaderAdditionalText: i18n.message('smarthelp/complaints.complaintsPage.nextHeaderAdditionalText'),
  finalResponseHeader: i18n.message('smarthelp/complaints.complaintsPage.finalResponseHeader'),
  finalResponseText: i18n.message('smarthelp/complaints.complaintsPage.finalResponseText'),
  finalResponseDescription: i18n.message('smarthelp/complaints.complaintsPage.finalResponseDescription'),
  finalResponseDescription1: i18n.message('smarthelp/complaints.complaintsPage.finalResponseDescription1'),
  finalResponseDisputeText: i18n.message('smarthelp/complaints.complaintsPage.finalResponseDisputeText'),
  finalResponseComplaintSpecificText: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseComplaintSpecificText',
  ),
  finalResponseDescriptionText: i18n.message('smarthelp/complaints.complaintsPage.finalResponseDescriptionText'),
  finalResponseDescriptionExtraTextForCZ: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForCZ',
  ),
  finalResponseDescriptionExtraTextForBE: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForBE',
  ),
  finalResponseDescriptionExtraTextForES1: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForES1',
  ),
  finalResponseDescriptionExtraTextForES2: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForES2',
  ),
  finalResponseDescriptionExtraTextForRO1: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForRO1',
  ),
  finalResponseDescriptionExtraTextForRO2: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForRO2',
  ),
  finalResponseDescriptionExtraTextForLV1: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForLV1',
  ),
  finalResponseDescriptionExtraTextForLV2: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForLV2',
  ),
  finalResponseDescriptionExtraTextForLV3: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionExtraTextForLV3',
  ),
  finalResponseDescriptionTextForUA: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionTextForUA',
  ),
  finalResponseDescriptionLinkTextForUA: i18n.message(
    'smarthelp/complaints.complaintsPage.finalResponseDescriptionLinkTextForUA',
  ),
  extendedHeading: i18n.message('smarthelp/complaints.complaintsPage.extendedHeading'),
  extendedHeadingBelowText: i18n.message('smarthelp/complaints.complaintsPage.extendedHeadingBelowText'),
  extendedHeadingFinalText: i18n.message('smarthelp/complaints.complaintsPage.extendedHeadingFinalText'),
};

const numberOfExtraTextForLV = 3;
const numberOfExtraTextForES = 2;
const numberOfExtraTextForRO = 2;

const countriesExtraHyperLinkContentAdded = ['dk', 'ie', 'it', 'lt', 'pt'];
const countriesExtraTextAdded = {
  LV: 'lv',
  CZ: 'cz',
  BE: 'be',
  ES: 'es',
  RO: 'ro',
};

/**
 * UK Complaints Page
 */
const BrexitComponent = function (props) {
  const { complaintsLinkClick, renderSubmitComplaint } = props;
  const complaintsTextInfo = getComplaintsPageInfo();
  const country = Page.getCountry().toLowerCase();
  const cssfCustComplaintsFRMarkets = _get(complaintsTextInfo, 'cssfCustComplaintsFRMarkets', []);

  const handleCssfComplaintUrl = () => {
    // French speaking countries -> return FR complaint url
    if (cssfCustComplaintsFRMarkets.includes(country.toUpperCase()))
      return complaintsTextInfo.cssfComplaintMessageUrlFR;
    return complaintsTextInfo.cssfComplaintMessageUrl;
  };

  // Render the extra texts in Final Response List on complaints page - Multiple Extra Texts
  const renderExtraTextsForCountry = (countryUpperCase, noOfextraTexts) => {
    return [...new Array(noOfextraTexts)].map((_, i) => (
      <li>
        <BodyText data-testid="sh-brexit-extra-text-after-final-resp-body-text">
          {Page.getHTMLElement({
            message: complaintsTranslations[`finalResponseDescriptionExtraTextFor${countryUpperCase}${i + 1}`],
            useHTML: true,
          })}
          &nbsp;
          <Link
            href={complaintsTextInfo[`finalResponseExtraUrlFor${countryUpperCase}${i + 1}`]}
            onClick={() =>
              complaintsLinkClick(complaintsTextInfo[`finalResponseExtraUrlFor${countryUpperCase}${i + 1}`])
            }
            target="_blank"
            rel="noopener noreferrer"
            data-testid="sh-brexit-extra-text-after-final-resp-link"
          >
            {complaintsTextInfo[`finalResponseExtraUrlFor${countryUpperCase}${i + 1}`]}
          </Link>
        </BodyText>
      </li>
    ));
  };

  // Render the extra text in Final Response List on complaints page - One Extra Text
  const renderExtraTextForCountry = (countryUpperCase) => {
    return (
      <li>
        <BodyText data-testid="sh-brexit-extra-text-after-final-resp-body-text">
          {Page.getHTMLElement({
            message: complaintsTranslations[`finalResponseDescriptionExtraTextFor${countryUpperCase}`],
            useHTML: true,
          })}
        </BodyText>
      </li>
    );
  };

  const renderExtraTextContentAfterFinalResponse = () => {
    switch (country) {
      case countriesExtraTextAdded.LV:
        return renderExtraTextsForCountry(country.toUpperCase(), numberOfExtraTextForLV);
      case countriesExtraTextAdded.ES:
        return renderExtraTextsForCountry(country.toUpperCase(), numberOfExtraTextForES);
      case countriesExtraTextAdded.RO:
        return renderExtraTextsForCountry(country.toUpperCase(), numberOfExtraTextForRO);
      case countriesExtraTextAdded.CZ:
        return renderExtraTextForCountry(country.toUpperCase());
      case countriesExtraTextAdded.BE:
        return renderExtraTextForCountry(country.toUpperCase());
      default:
        return null;
    }
  };

  const renderExtraHyperLinkContentAfterFinalResponse = () => {
    if (countriesExtraHyperLinkContentAdded.find((ct) => ct === country)) {
      const countryUpperCase = country.toUpperCase();
      return (
        <li>
          <BodyText>
            {Page.getHTMLElement({ message: complaintsTranslations.finalResponseDescriptionTextForUA, useHTML: true })}
            &nbsp;
            <Link
              href={complaintsTextInfo[`finalResponseUALinkFor${countryUpperCase}`]}
              onClick={() => complaintsLinkClick(complaintsTextInfo[`finalResponseUALinkFor${countryUpperCase}`])}
              target="_blank"
              rel="noopener noreferrer"
            >
              {complaintsTranslations.finalResponseDescriptionLinkTextForUA}
            </Link>
          </BodyText>
        </li>
      );
    }

    return null;
  };

  const handleHereClickEvent = (e) => {
    e.preventDefault();
    complaintsLinkClick(COMPLAINTS_PROCESS_CODE_LINK_SK);
    window.open(COMPLAINTS_PROCESS_CODE_LINK_SK, '_blank');
  };

  // additional text and link (link to complaints process code) on "what happens next" para for Slovakia
  const renderAdditionalTextAndLinkSK = () => {
    return (
      <>
        {complaintsTranslations.nextHeaderAdditionalText}
        &nbsp;
        <Link
          href="#"
          onClick={handleHereClickEvent}
          rel="noopener noreferrer"
          data-testid="sh-brexit-complaints-process-code-for-slovakia-link"
        >
          {complaintsTranslations.hereText}.
        </Link>
      </>
    );
  };

  // Displaying content from translations for country/locale in Remote config
  if (isComplaintsCountryLocaleEnabled()) {
    return (
      <Container className="brexit-complaints-page">
        <div className="brexit-complaints-page-div">
          <BodyText className="brexit-complaints-page-div__intro-text">{complaintsTranslations.introText}</BodyText>
          {renderSubmitComplaint}
          <HeadingText className="brexit-complaints-page-div__next-header">
            {complaintsTranslations.nextHeader}
          </HeadingText>
          <BodyText
            className="brexit-complaints-page-div__response-text"
            data-testid="sh-brexit-complaints-what-happens-next-body-text"
          >
            {complaintsTranslations.nextHeaderText}
            {country === 'sk' && renderAdditionalTextAndLinkSK()}
          </BodyText>
          <HeadingText className="brexit-complaints-page-div__after-response-header">
            {complaintsTranslations.finalResponseHeader}
          </HeadingText>
          <BodyText className="brexit-complaints-page-div__after-response-text">
            {complaintsTranslations.finalResponseText}
            <ul>
              <li>
                <BodyText>
                  {complaintsTranslations.finalResponseDescription}
                  <Link
                    href={complaintsTextInfo.cssfUrl}
                    onClick={() => complaintsLinkClick(complaintsTextInfo.cssfUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {complaintsTextInfo.cssfUrl}
                  </Link>
                  &nbsp;
                  {complaintsTranslations.finalResponseDisputeText}
                  <Link
                    href={complaintsTextInfo.euUrl}
                    onClick={() => complaintsLinkClick(complaintsTextInfo.euUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {complaintsTextInfo.euUrl}
                  </Link>
                  . &nbsp;
                  {complaintsTranslations.finalResponseComplaintSpecificText}
                  <Link
                    href={handleCssfComplaintUrl()}
                    onClick={() => complaintsLinkClick(handleCssfComplaintUrl())}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {handleCssfComplaintUrl()}
                  </Link>
                  .
                </BodyText>
              </li>
              <li>
                <BodyText>
                  {Page.getHTMLElement({ message: complaintsTranslations.finalResponseDescriptionText, useHTML: true })}
                  <Link
                    href={complaintsTextInfo.resolveComplaintsUrl}
                    onClick={() => complaintsLinkClick(complaintsTextInfo.resolveComplaintsUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {complaintsTextInfo.resolveComplaintsUrl}
                  </Link>
                  .
                </BodyText>
              </li>
              {renderExtraTextContentAfterFinalResponse()}
              {renderExtraHyperLinkContentAfterFinalResponse()}
            </ul>
          </BodyText>
        </div>
      </Container>
    );
  }

  // Displaying content from Remote config for UK market
  return (
    <Container className="brexit-complaints-page">
      <div className="brexit-complaints-page-div">
        <BodyText className="brexit-complaints-page-div__intro-text">{complaintsTextInfo.introText}</BodyText>
        {renderSubmitComplaint}
        <HeadingText className="brexit-complaints-page-div__next-header">{complaintsTextInfo.nextLabel}</HeadingText>
        <BodyText className="brexit-complaints-page-div__response-text">{complaintsTextInfo.responseText}</BodyText>
        <HeadingText className="brexit-complaints-page-div__after-response-header">
          {complaintsTextInfo.afterResponseLabel}
        </HeadingText>
        <BodyText className="brexit-complaints-page-div__after-response-text">
          {complaintsTextInfo.afterResponseTextUpdate} {complaintsTextInfo.afterResponseTextUpdateBold}
        </BodyText>
        <BodyText className="brexit-complaints-page-div__after-response-text">
          {complaintsTextInfo.fosMessage}
          <Link
            href={complaintsTextInfo.fosUrl}
            onClick={() => complaintsLinkClick(complaintsTextInfo.fosUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {complaintsTextInfo.fosUrl}
          </Link>
          {complaintsTextInfo.fosMessageEnd}
        </BodyText>
        <BodyText className="brexit-complaints-page-div__after-response-text">
          {complaintsTextInfo.orAlternatively}
        </BodyText>
        <BodyText className="brexit-complaints-page-div__after-response-text">
          {complaintsTextInfo.cssfMessage}
          <Link
            href={complaintsTextInfo.cssfUrl}
            onClick={() => complaintsLinkClick(complaintsTextInfo.cssfUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {complaintsTextInfo.cssfUrl}
          </Link>
          .&nbsp;
          {complaintsTextInfo.cssfComplaintMessage}
          <Link
            href={handleCssfComplaintUrl()}
            onClick={() => complaintsLinkClick(handleCssfComplaintUrl())}
            target="_blank"
            rel="noopener noreferrer"
          >
            {handleCssfComplaintUrl()}
          </Link>
          .
        </BodyText>
        <HeadingText className="brexit-complaints-page-div__complaints-data-header">
          {complaintsTextInfo.complaintsData}
        </HeadingText>
        <BodyText className="brexit-complaints-page-div__complaints-data-summary">
          {complaintsTextInfo.complaintsSummary}
          <Link
            href={complaintsTextInfo.complaintsDataUrl}
            onClick={() => complaintsLinkClick(complaintsTextInfo.complaintsDataUrl)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {complaintsTextInfo.complaintsDataLinkText}
          </Link>
          {complaintsTextInfo.complaintsSummaryDuration}
        </BodyText>
      </div>
    </Container>
  );
};

BrexitComponent.propTypes = {
  complaintsLinkClick: PropTypes.func.isRequired,
  renderSubmitComplaint: PropTypes.func.isRequired,
};

export default BrexitComponent;
